/**
 * MyWithDrawalDetails
 *
 * Description: This component displays the user's withdrawal details, including the current balance, total withdrawal amounts,
 * and a list of withdrawal requests. It allows users to create new withdrawal requests and view their withdrawal history.
 *
 * File Name: MyWithDrawalDetails.tsx
 * Date: 30-09-2024
 */

import React, { FormEvent, useEffect, useState } from "react";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import { IAccountInfo, IqueryParams } from "src/types/GlobalInterfaces";
import CustomerTableWrapper from "../Customer/CustomerTableWrapper";
import { IPaginated } from "src/types/paginated";
import { CURRENCY, statusColor } from "src/lib/constans";
import PaymentInfo from "./PaymentInfo";
import SimpleInput from "../SimpleInput";
import folderArrow from "src/assets/images/folder-arrow.png";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faStar } from "@fortawesome/free-solid-svg-icons";
import qs from "qs";
import { displayStatus } from "src/helpers/helperFunctions";
import SearchFilterInput from "../GlobalComponents/SearchFilterInput";
import CustomWebsiteDropdown from "../GlobalComponents/Widget/CustomWebsiteDropdown";
import { Option } from "react-multi-select-component";
const backBtnMaain: React.CSSProperties = {
  position: "absolute",
  top: -10,
  right: -10,
  backgroundColor: "red",
  borderRadius: 100,
  width: 20,
  height: 20,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};
const backBtn: React.CSSProperties = {
  color: "white",
};
interface IWallet {
  totalAmount: number;
  currentBalance: number;
  totalWithdrawAmount: number;
  pendingWithdraw: number;
  [key: string]: any;
}

interface IItem {
  _id: string;
  amount: number;
  status: "completed" | "pending" | "declined";
  attachment?: any;
  reason?: string;
  [key: string]: any;
}

interface IWithDrawalResponse {
  documents: IItem[];
  paginated: IPaginated;
}

const MyWithDrawalDetails: React.FC<{ accountInfo: IAccountInfo | null }> = ({
  accountInfo = null,
}) => {
  const [isRequestMode, setIsRequestMode] = useState<boolean>(false);
  const [totalEarning, setTotalEarnings] = useState<any>("");

  // ****************** create new request functions & state ****************** //
  const [createLoader, setCreateLoader] = useState<boolean>(false);
  const [amount, setAmount] = useState<string>("");

  const [fileView, setFileView] = useState<any>(null);
  const [errors, setErrors] = useState<{ withdrawalAmount: string }>({
    withdrawalAmount: "",
  });
  const fetchStats = async () => {
    const { res, err } = await httpRequest<any>({
      path: "/accessdoctorside/statistic",
    });
    if (res) {
      const { totalCustomer, totalEarning, monthlyData, yearlyDataDoctor } =
        res[0];
      setTotalEarnings(totalEarning[0]?.totalAmount || 0);
    } else {
      toast(err, { type: "error" });
    }
  };
  useEffect(() => {
    fetchStats();
  }, []);
  const handleOpenModal = (fileUrl: string) => {
    const isPDF = fileUrl.endsWith(".pdf");
    if (isPDF) {
      window.open(`${fileUrl}`, "_blank");
    } else {
      setFileView(fileUrl);
    }
  };

  const handleCloseModal = () => {
    setFileView(null);
  };
  // API_CALL: create new withdraw request
  const createNewRequest = async (event: FormEvent) => {
    event.preventDefault();

    if (parseFloat(amount || "0") > currentBalance) {
      return toast.error(
        "Insufficient balance. The amount exceeds your current balance."
      );
    }

    if (parseFloat(amount || "0") === 0) {
      return;
    }

    setCreateLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "post",
      path: "/paymentandwithdraw/create",
      params: { amount },
    });
    if (res) {
      toast(res.message || "Success", { type: "success" });
      setIsRequestMode(false);
      setAmount("");
    } else {
      toast(err, { type: "error" });
    }
    setCreateLoader(false);
  };
  // ****************** create new request functions & state ****************** //

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [data, setData] = useState<IItem[]>([]);
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 10,
  });

  const checkBankInfoExist = (): boolean => {
    if (
      !accountInfo?.accountHolderName ||
      !accountInfo?.accountNumber ||
      !accountInfo?.bankName
    ) {
      return false;
    } else {
      return true;
    }
  };

  // Handles page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  // API_CALL: fetch request list
  const fetchWithDrawalList = async () => {
    setIsLoader(true);
    const { res, err } = await httpRequest<IWithDrawalResponse>({
      path: `/paymentandwithdraw/all/for-doctors?${qs.stringify(queryParams)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      setData(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  const [walletData, setWalletData] = useState<IWallet | any>(null);
  // API_CALL: fetch wallet data
  const fetchWallet = async () => {
    setIsLoader(true);
    // const { res, err } = await httpRequest<IWallet>({ path: `/paymentandwithdraw/wallet` });
    const { res, err } = await httpRequest<IWallet>({
      path: `/paymentandwithdraw/view-wallet-details`,
    });

    if (res) {
      setWalletData(res?.document);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    if (!isRequestMode) {
      fetchWallet();
    }
  }, [isRequestMode]);

  useEffect(() => {
    if (!isRequestMode) {
      fetchWithDrawalList();
    }
  }, [queryParams, isRequestMode]);

  let currentBalance =
    totalEarning -
    (walletData?.totalWithdrawAmount || 0) -
    (walletData?.pendingWithdrawAmount || 0);

  const handlewithdrawl = () => {
    if (currentBalance > 0) {
      setIsRequestMode(true);
    } else {
      toast("You have insufficient balance", { type: "error" });
    }
  };

  const statusList = [{
    label: "Completed",
    value: "completed",
  }, {
    label: "Pending",
    value: "pending",
  }, {
    label: "Declined",
    value: "declined",
  }];

  return (
    <>
      {isRequestMode ? (
        <div className="max-w-[600px]">
          <PaymentInfo accountInfo={accountInfo} />
          <form onSubmit={createNewRequest}>
            <SimpleInput
              value={amount}
              onChange={(val) => {
                if (parseFloat(val || "0") > currentBalance) {
                  setErrors((prev) => ({
                    ...prev,
                    withdrawalAmount:
                      "Insufficient balance. The amount exceeds your current balance.",
                  }));
                } else {
                  setErrors((prev) => ({
                    ...prev,
                    withdrawalAmount: "",
                  }));
                }
                setAmount(val);
              }}
              isLabel={true}
              labelText="Withdraw Amount"
              inputMode="numeric"
              extraStyle={{ borderColor: "#009d91" }}
              isRequired={true}
            />
            {errors.withdrawalAmount && (
              <p className="font-size-14px -mt-2 mb-2 text-[#ff0000] font-Poppins-Regular">
                {errors.withdrawalAmount}
              </p>
            )}
            <div className="flex gap-2 items-start mb-3">
              <input type="checkbox" className="mt-1" required={true} />
              <p className="text-sm">
                I confirm that I agree to these payment details, that they are
                accurate, and that funds can be transferred to this account as
                agreed.
              </p>
            </div>
            <div className="flex justify-end">
              <PrimaryBtn
                loader={createLoader}
                type="submit"
                ButtonText="Submit"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "50px",
                  padding: "0.5rem 2rem",
                }}
              />
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between my-4">
            <div className="Withdrawal-box-revenue">
              <div>
                <p className="text-[#585858] font-size-18px font-Poppins-Regular">
                  Current Balance
                </p>
                <strong className="text-[#373737] font-size-27px font-Poppins-SemiBold">
                  {CURRENCY} {currentBalance.toFixed(2)}
                </strong>
              </div>
              <div>
                <img src={folderArrow} alt="folder arrow" />
              </div>
            </div>
            <div className="Withdrawal-box-revenue">
              <div>
                <p className="text-[#585858] font-size-18px font-Poppins-Regular">
                  Total Withdrawal
                </p>
                <strong className="text-[#373737] font-size-27px font-Poppins-SemiBold">
                  {CURRENCY} {walletData?.totalWithdrawAmount || 0}
                </strong>
              </div>
              <div>
                <img src={folderArrow} alt="folder arrow" />
              </div>
            </div>
            <div className="Withdrawal-box-revenue">
              <div>
                <p className="text-[#585858] font-size-18px font-Poppins-Regular">
                  Pending Withdrawal
                </p>
                <strong className="text-[#373737] font-size-27px font-Poppins-SemiBold">
                  {CURRENCY} {walletData?.pendingWithdrawAmount || 0}
                </strong>
              </div>
              <div>
                <img src={folderArrow} alt="folder arrow" />
              </div>
            </div>
            {/* <div className="Withdrawal-box-revenue">
              <div>
                <p className="text-[#585858] font-size-16px font-Poppins-Regular">
                  Total Earnings
                </p>
                <strong className="text-[#373737] font-size-27px font-Poppins-SemiBold">
                  {CURRENCY}{" "}
                  {totalEarning
                    ? parseFloat(totalEarning || "0")?.toFixed(2)
                    : 0}
                </strong>
              </div>
              <div>
                <img src={folderArrow} alt="folder arrow" />
              </div>
            </div> */}
          </div>
          <hr className="products-tabs-hr-allOrder" />
          <div className="flex items-center justify-between 2xl:mb-4 mb-3">
            {/* <SearchFilterInput
              containerExtraStyle={{ display: "block" }}
              inputExtraStyle={{ width: "100%", marginBottom: 0 }}
              onChangeText={(val: string) => setQueryParams((prev) => ({ ...prev, search: val || "" }))}
              placeHolder={"Search"}
            /> */}
            <CustomWebsiteDropdown
              options={statusList}
              data={statusList.filter((v) => v.value === queryParams.search)}
              setData={(data: Option[]) => setQueryParams((prev) => ({ ...prev, search: data[0]?.value || "" }))}
              mainDivClass="!max-w-[200px]"
              withoutMargin
              placeholder="Filter by Status"
            />

            <PrimaryBtn
              title={
                checkBankInfoExist()
                  ? ""
                  : "Filling in your bank information is mandatory before submitting a withdrawal request."
              }
              disabled={checkBankInfoExist() ? false : true}
              btnExtralClasses={
                checkBankInfoExist() ? "" : "cursor-not-allowed"
              }
              ButtonText="Withdrawal Request"
              onPress={handlewithdrawl}
              btnExtraStyle={{
                backgroundColor: "#00443f",
                borderRadius: "1.5rem",
                padding: "7px 25px",
              }}
            />
          </div>
          <CustomerTableWrapper
            isLoading={isLoader}
            thead={[
              "S.No",
              "Requested Amount",
              "Status",
              "Verification Document",
              "Comment",
              "",
            ]}
            page={paginated.currentPage || 1}
            total={paginated.totalPages || 0}
            columnWidth={"8% 17% 17% 20% 38% 0%"}
            onChange={handlePageChange}
          >
            {data.map((item: IItem, index) => (
              <ul
                key={index}
                className={` font-size-14px tbody-light-table customer-table-wrapper-body-padding-non customer-table-wrapper-body tbody-light-table-customer font-Poppins-Regular grid`}
                style={{ gridTemplateColumns: "8% 17% 17% 20% 38% 0%" }}
              >
                <li style={{ paddingLeft: "2rem" }}>
                  {(paginated?.currentPage - 1) * queryParams.limit +
                    (index + 1)}
                </li>
                <li>{CURRENCY} {item?.amount || ""}</li>
                <li>
                  <span
                    className="py-1 px-4 text-white capitalize rounded"
                    // style={{
                    //     backgroundColor:
                    //         item.status === "pending" ? statusColor.yellow
                    //             : item.status === "completed" ? statusColor.green
                    //                 : item.status === "declined" ? statusColor.red
                    //                     : ""
                    // }}
                    style={{
                      backgroundColor: displayStatus(item?.status)?.bgColor,
                      color: displayStatus(item?.status)?.color,
                    }}
                  >
                    {item.status}
                  </span>
                </li>
                <li style={{ justifyContent: "center" }}>
                  {item.status === "completed" ? (
                    <img
                      onClick={() => handleOpenModal(item?.attachment)}
                      className="img-recepit"
                      src={`${item?.attachment}`}
                      alt="attachement"
                    />
                  ) : (
                    "-"
                  )}
                </li>
                <li>
                  <span className="p-2">
                    <p className="line-clamp-2">{item?.reason || ""}</p>
                  </span>
                </li>
                <li></li>
              </ul>
            ))}
          </CustomerTableWrapper>

          {fileView && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]"
              onClick={handleCloseModal}
            >
              <div
                className="Prescription-modal-voice"
                onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside the modal
              >
                <button onClick={handleCloseModal} style={backBtnMaain}>
                  <FontAwesomeIcon
                    className="font-size-16px "
                    icon={faXmark}
                    size="2x"
                    style={backBtn}
                  />
                </button>
                <img
                  src={`${fileView}`}
                  alt="Prescription"
                  className=""
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default MyWithDrawalDetails;
